<script setup lang="ts">
import { FolderOpen, Users } from 'lucide-vue-next';
import { onMounted, ref, watch } from 'vue';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/composables/useToast';
import * as zoteroRoutes from '@/routes/zotero';

interface Collection {
    key: string;
    name: string;
    parentCollection?: string;
}

interface Group {
    id: number;
    name: string;
}

const props = defineProps<{
    open: boolean;
    projectId: string;
}>();

const emit = defineEmits<{
    'update:open': [value: boolean];
    started: [runId: string];
}>();

const toast = useToast();
const tab = ref<'library' | 'groups'>('library');
const collections = ref<Collection[]>([]);
const groups = ref<Group[]>([]);
const loadingCollections = ref(false);
const loadingGroups = ref(false);
const selectedCollectionKey = ref<string | null>(null);
const selectedGroupId = ref<number | null>(null);
const importing = ref(false);

async function loadCollections() {
    if (collections.value.length > 0) {
        return;
    }

    loadingCollections.value = true;

    try {
        const res = await fetch(zoteroRoutes.collections().url);

        if (!res.ok) {
            throw new Error();
        }

        const data = await res.json();
        collections.value = Array.isArray(data)
            ? data.map((c: Record<string, unknown>) => {
                  const d = (c['data'] as Record<string, unknown>) ?? c;

                  return {
                      key: (c['key'] as string) ?? '',
                      name: (d['name'] as string) ?? '',
                      parentCollection:
                          (d['parentCollection'] as string | undefined) ??
                          undefined,
                  };
              })
            : [];
    } catch {
        toast.error('Impossible de charger les collections Zotero.');
    } finally {
        loadingCollections.value = false;
    }
}

async function loadGroups() {
    if (groups.value.length > 0) {
        return;
    }

    loadingGroups.value = true;

    try {
        const res = await fetch(zoteroRoutes.groups().url);

        if (!res.ok) {
            throw new Error();
        }

        const data = await res.json();
        groups.value = Array.isArray(data)
            ? data.map((g: Record<string, unknown>) => {
                  const d = (g['data'] as Record<string, unknown>) ?? g;

                  return {
                      id: (g['id'] as number) ?? (d['id'] as number) ?? 0,
                      name: (d['name'] as string) ?? '',
                  };
              })
            : [];
    } catch {
        toast.error('Impossible de charger les groupes Zotero.');
    } finally {
        loadingGroups.value = false;
    }
}

onMounted(() => {
    if (props.open) {
        loadCollections();
    }
});

watch(
    () => props.open,
    (open) => {
        if (open) {
            loadCollections();
        }
    },
);

watch(tab, (t) => {
    if (t === 'groups') {
        loadGroups();
    }
});

// Top-level collections first, then children indented
const rootCollections = () =>
    collections.value.filter((c) => !c.parentCollection);

const childCollections = (parentKey: string) =>
    collections.value.filter((c) => c.parentCollection === parentKey);

async function startImport() {
    importing.value = true;

    try {
        const body: Record<string, unknown> = { project_id: props.projectId };

        if (tab.value === 'library' && selectedCollectionKey.value) {
            body.collection_key = selectedCollectionKey.value;
        }

        if (tab.value === 'groups' && selectedGroupId.value) {
            body.group_id = selectedGroupId.value;
        }

        const res = await fetch(zoteroRoutes.importMethod().url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN':
                    (
                        document.querySelector(
                            'meta[name="csrf-token"]',
                        ) as HTMLMetaElement
                    )?.content ?? '',
                Accept: 'application/json',
            },
            body: JSON.stringify(body),
        });

        if (res.status === 409) {
            toast.info('Un import est déjà en cours pour ce projet.');

            return;
        }

        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            toast.error(
                (err as { message?: string }).message ??
                    "Erreur lors du démarrage de l'import.",
            );

            return;
        }

        const run = await res.json();
        emit('update:open', false);
        emit('started', run.id as string);
    } catch {
        toast.error("Erreur réseau lors du démarrage de l'import.");
    } finally {
        importing.value = false;
    }
}
</script>

<template>
    <Dialog :open="open" @update:open="emit('update:open', $event)">
        <DialogContent class="max-w-md">
            <DialogHeader>
                <DialogTitle>Importer depuis Zotero</DialogTitle>
                <DialogDescription>
                    Choisissez la source à importer dans ce projet.
                </DialogDescription>
            </DialogHeader>

            <!-- Tabs -->
            <div class="flex rounded-md border border-border bg-muted/50 p-0.5">
                <button
                    class="flex flex-1 items-center justify-center gap-1.5 rounded py-1.5 text-xs font-medium transition-colors"
                    :class="
                        tab === 'library'
                            ? 'bg-background text-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground'
                    "
                    @click="tab = 'library'"
                >
                    <FolderOpen class="h-3.5 w-3.5" />
                    Ma bibliothèque
                </button>
                <button
                    class="flex flex-1 items-center justify-center gap-1.5 rounded py-1.5 text-xs font-medium transition-colors"
                    :class="
                        tab === 'groups'
                            ? 'bg-background text-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground'
                    "
                    @click="tab = 'groups'"
                >
                    <Users class="h-3.5 w-3.5" />
                    Groupes
                </button>
            </div>

            <!-- Library tab -->
            <div v-if="tab === 'library'" class="min-h-[180px]">
                <p class="mb-3 text-xs text-muted-foreground">
                    Sélectionnez une collection à importer.
                </p>
                <div
                    v-if="loadingCollections"
                    class="py-6 text-center text-xs text-muted-foreground"
                >
                    Chargement des collections…
                </div>
                <ul
                    v-else
                    class="max-h-52 space-y-0.5 overflow-y-auto rounded-md border border-border"
                >
                    <!-- Root collections -->
                    <template v-for="col in rootCollections()" :key="col.key">
                        <li>
                            <button
                                class="flex w-full items-center gap-2 px-3 py-2 text-left text-xs transition-colors hover:bg-muted/60"
                                :class="
                                    selectedCollectionKey === col.key
                                        ? 'bg-accent/80 font-medium text-foreground'
                                        : 'text-muted-foreground hover:text-foreground'
                                "
                                @click="selectedCollectionKey = col.key"
                            >
                                {{ col.name }}
                            </button>
                        </li>
                        <!-- Child collections (1 level) -->
                        <li
                            v-for="child in childCollections(col.key)"
                            :key="child.key"
                        >
                            <button
                                class="flex w-full items-center gap-2 border-t border-border/40 py-2 pr-3 pl-6 text-left text-xs transition-colors hover:bg-muted/60"
                                :class="
                                    selectedCollectionKey === child.key
                                        ? 'bg-accent/80 font-medium text-foreground'
                                        : 'text-muted-foreground hover:text-foreground'
                                "
                                @click="selectedCollectionKey = child.key"
                            >
                                {{ child.name }}
                            </button>
                        </li>
                    </template>
                    <li
                        v-if="!loadingCollections && collections.length === 0"
                        class="px-3 py-4 text-center text-xs text-muted-foreground italic"
                    >
                        Aucune collection dans votre bibliothèque.
                    </li>
                </ul>
            </div>

            <!-- Groups tab -->
            <div v-else class="min-h-[180px]">
                <p class="mb-3 text-xs text-muted-foreground">
                    Sélectionnez un groupe Zotero à importer.
                </p>
                <div
                    v-if="loadingGroups"
                    class="py-6 text-center text-xs text-muted-foreground"
                >
                    Chargement des groupes…
                </div>
                <ul
                    v-else
                    class="max-h-52 space-y-0.5 overflow-y-auto rounded-md border border-border"
                >
                    <li v-for="group in groups" :key="group.id">
                        <button
                            class="flex w-full items-center gap-2 px-3 py-2 text-left text-xs transition-colors hover:bg-muted/60"
                            :class="
                                selectedGroupId === group.id
                                    ? 'bg-accent/80 font-medium text-foreground'
                                    : 'text-foreground'
                            "
                            @click="selectedGroupId = group.id"
                        >
                            {{ group.name }}
                        </button>
                    </li>
                    <li
                        v-if="!loadingGroups && groups.length === 0"
                        class="px-3 py-4 text-center text-xs text-muted-foreground italic"
                    >
                        Aucun groupe trouvé.
                    </li>
                </ul>
            </div>

            <DialogFooter class="gap-2">
                <Button
                    variant="outline"
                    size="sm"
                    @click="emit('update:open', false)"
                >
                    Annuler
                </Button>
                <Button
                    size="sm"
                    :disabled="
                        importing ||
                        (tab === 'library' && selectedCollectionKey === null) ||
                        (tab === 'groups' && selectedGroupId === null)
                    "
                    @click="startImport"
                >
                    {{ importing ? 'Démarrage…' : 'Importer' }}
                </Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>
